import { useState, useEffect, useRef } from 'react';
import { apiClient, ApiHttpError } from '../api/client';

export type XamanPayloadStatus =
  | 'pending'           // Initial state
  | 'opened'            // User opened in Xaman app
  | 'signing'           // User is signing
  | 'dispatching'       // Transaction being sent to XRPL
  | 'signed'            // Successfully signed
  | 'rejected'          // User rejected
  | 'expired';          // Payload expired

export interface XamanPayloadState {
  status: XamanPayloadStatus;
  txid?: string;
  error?: string;
  isWebSocket: boolean;
  expiresInSeconds?: number;
}

interface UseXamanPayloadStatusOptions {
  uuid: string;
  websocketUrl?: string;
  onSigned?: (txid: string) => void;
  onRejected?: () => void;
  onExpired?: () => void;
  pollingInterval?: number;
  maxPollingAttempts?: number;
  websocketTimeout?: number;
}

// Max backoff interval when rate-limited (15 seconds)
const MAX_BACKOFF_MS = 15000;

/**
 * Hook to monitor Xaman payload status using WebSocket with HTTP polling fallback
 *
 * @param options - Configuration options
 * @returns Current payload state
 */
export function useXamanPayloadStatus({
  uuid,
  websocketUrl,
  onSigned,
  onRejected,
  onExpired,
  pollingInterval = 3000,
  maxPollingAttempts = 40,
  websocketTimeout = 5000,
}: UseXamanPayloadStatusOptions): XamanPayloadState {
  const [state, setState] = useState<XamanPayloadState>({
    status: 'pending',
    isWebSocket: false,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const websocketTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingAttemptsRef = useRef(0);
  const currentIntervalRef = useRef(pollingInterval);
  const isCleanedUpRef = useRef(false);

  // Guard against duplicate callback invocations
  const callbackInvokedRef = useRef(false);

  // Track status via ref to avoid stale closure in ws.onclose
  const statusRef = useRef<XamanPayloadStatus>('pending');

  // Keep statusRef in sync with state for use in closures
  statusRef.current = state.status;

  console.log(`[useXamanPayloadStatus] Hook initialized for uuid: ${uuid}, hasWebsocket: ${!!websocketUrl}`);

  useEffect(() => {
    console.log(`[useXamanPayloadStatus] useEffect running for uuid: ${uuid}`);
    isCleanedUpRef.current = false;
    callbackInvokedRef.current = false; // Reset callback guard for new payload
    currentIntervalRef.current = pollingInterval; // Reset backoff

    // Try WebSocket first if URL is provided
    if (websocketUrl) {
      tryWebSocket();
    } else {
      // Fall back to polling immediately if no WebSocket URL
      startPolling();
    }

    return () => {
      console.log(`[useXamanPayloadStatus] Cleanup running for uuid: ${uuid}`);
      cleanup();
    };
  }, [uuid, websocketUrl]);

  const cleanup = () => {
    isCleanedUpRef.current = true;

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Clear polling timeout
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    // Clear WebSocket timeout
    if (websocketTimeoutRef.current) {
      clearTimeout(websocketTimeoutRef.current);
      websocketTimeoutRef.current = null;
    }
  };

  const tryWebSocket = () => {
    if (!websocketUrl || isCleanedUpRef.current) return;

    console.log('[useXamanPayloadStatus] Attempting WebSocket connection:', websocketUrl);

    try {
      const ws = new WebSocket(websocketUrl);
      wsRef.current = ws;

      // Set timeout to fall back to polling if WebSocket doesn't connect
      websocketTimeoutRef.current = setTimeout(() => {
        if (wsRef.current?.readyState !== WebSocket.OPEN) {
          console.log('[useXamanPayloadStatus] WebSocket timeout, falling back to polling');
          cleanup();
          startPolling();
        }
      }, websocketTimeout);

      ws.onopen = () => {
        // Only process if this is still the current WebSocket
        if (isCleanedUpRef.current || wsRef.current !== ws) return;
        console.log('[useXamanPayloadStatus] WebSocket connected');

        // Clear timeout - WebSocket connected successfully
        if (websocketTimeoutRef.current) {
          clearTimeout(websocketTimeoutRef.current);
          websocketTimeoutRef.current = null;
        }

        setState(prev => ({ ...prev, isWebSocket: true }));
      };

      ws.onmessage = (event) => {
        // Only process if this is still the current WebSocket
        if (isCleanedUpRef.current || wsRef.current !== ws) return;

        try {
          const data = JSON.parse(event.data);
          console.log('[useXamanPayloadStatus] WebSocket message:', data);

          // Welcome message
          if (data.message) {
            return;
          }

          // Keep-alive
          if (data.expires_in_seconds !== undefined) {
            setState(prev => ({ ...prev, expiresInSeconds: data.expires_in_seconds }));
            return;
          }

          // User opened payload
          if (data.opened === true) {
            setState(prev => ({ ...prev, status: 'opened' }));
            return;
          }

          // User started signing
          if (data.pre_signed === true) {
            setState(prev => ({ ...prev, status: 'signing' }));
            return;
          }

          // Transaction dispatched to XRPL
          if (data.dispatched === true) {
            setState(prev => ({ ...prev, status: 'dispatching' }));
            return;
          }

          // Payload expired
          if (data.expired === true) {
            setState(prev => ({ ...prev, status: 'expired', error: 'Payload expired' }));
            // Guard against duplicate callback invocations
            if (!callbackInvokedRef.current) {
              callbackInvokedRef.current = true;
              console.log(`[useXamanPayloadStatus] WebSocket: Invoking onExpired callback`);
              onExpired?.();
            } else {
              console.log(`[useXamanPayloadStatus] WebSocket: SKIPPING duplicate onExpired callback`);
            }
            cleanup();
            return;
          }

          // Resolution (signed or rejected)
          if (data.signed !== undefined) {
            if (data.signed === true) {
              // WebSocket only tells us the user signed — it does NOT include
              // the XRPL dispatch result code. A tecNO_ENTRY or other TEC error
              // still arrives as signed:true. Verify via HTTP before calling onSigned.
              setState(prev => ({ ...prev, status: 'dispatching' }));
              // Close WebSocket to stop receiving messages, but do NOT call cleanup()
              // because verifyDispatchResult() checks isCleanedUpRef and would exit immediately.
              if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
              }
              if (websocketTimeoutRef.current) {
                clearTimeout(websocketTimeoutRef.current);
                websocketTimeoutRef.current = null;
              }
              verifyDispatchResult(data.txid);
            } else {
              setState(prev => ({
                ...prev,
                status: 'rejected',
                error: 'User rejected the transaction'
              }));
              // Guard against duplicate callback invocations
              if (!callbackInvokedRef.current) {
                callbackInvokedRef.current = true;
                console.log(`[useXamanPayloadStatus] WebSocket: Invoking onRejected callback`);
                onRejected?.();
              } else {
                console.log(`[useXamanPayloadStatus] WebSocket: SKIPPING duplicate onRejected callback`);
              }
              cleanup();
            }
          }
        } catch (err) {
          console.error('[useXamanPayloadStatus] Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        // Only process if this is still the current WebSocket
        if (isCleanedUpRef.current || wsRef.current !== ws) return;
        console.error('[useXamanPayloadStatus] WebSocket error:', {
          error,
          url: websocketUrl,
          readyState: ws.readyState,
          readyStateText: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.readyState]
        });
        cleanup();
        startPolling();
      };

      ws.onclose = () => {
        // Only process if this is still the current WebSocket
        if (isCleanedUpRef.current || wsRef.current !== ws) return;
        console.log('[useXamanPayloadStatus] WebSocket closed');

        // If we haven't reached a final state, fall back to polling
        const finalStates: XamanPayloadStatus[] = ['signed', 'rejected', 'expired'];
        if (!finalStates.includes(statusRef.current)) {
          startPolling();
        }
      };
    } catch (err) {
      console.error('[useXamanPayloadStatus] Failed to create WebSocket:', err);
      startPolling();
    }
  };

  const startPolling = () => {
    if (isCleanedUpRef.current || pollingTimeoutRef.current) return;

    console.log('[useXamanPayloadStatus] Starting HTTP polling fallback');
    setState(prev => ({ ...prev, isWebSocket: false }));

    // Poll immediately, then schedule next poll after completion
    pollStatus();
  };

  const scheduleNextPoll = () => {
    if (isCleanedUpRef.current) return;
    pollingTimeoutRef.current = setTimeout(() => {
      pollingTimeoutRef.current = null;
      pollStatus();
    }, currentIntervalRef.current);
  };

  const pollStatus = async () => {
    if (isCleanedUpRef.current) return;

    pollingAttemptsRef.current++;
    console.log(`[useXamanPayloadStatus] Polling attempt ${pollingAttemptsRef.current}/${maxPollingAttempts} (interval: ${currentIntervalRef.current}ms)`);

    // Check if we've exceeded max attempts
    if (pollingAttemptsRef.current > maxPollingAttempts) {
      console.log('[useXamanPayloadStatus] Polling timeout exceeded');
      setState(prev => ({
        ...prev,
        status: 'expired',
        error: 'Polling timeout - please try again'
      }));
      // Guard against duplicate callback invocations
      if (!callbackInvokedRef.current) {
        callbackInvokedRef.current = true;
        console.log(`[useXamanPayloadStatus] Polling: Invoking onExpired callback (timeout)`);
        onExpired?.();
      } else {
        console.log(`[useXamanPayloadStatus] Polling: SKIPPING duplicate onExpired callback`);
      }
      cleanup();
      return;
    }

    try {
      const response = await apiClient.get<any>(`/offers/payload/${uuid}`);
      console.log(`[useXamanPayloadStatus] Poll response:`, response);

      // Successful response — reset backoff to base interval
      currentIntervalRef.current = pollingInterval;

      if (!response.success || !response.data) {
        scheduleNextPoll();
        return;
      }

      const { signed, resolved, expired, txid, dispatched } = response.data;

      // Payload expired
      if (expired) {
        setState(prev => ({ ...prev, status: 'expired', error: 'Payload expired' }));
        // Guard against duplicate callback invocations
        if (!callbackInvokedRef.current) {
          callbackInvokedRef.current = true;
          console.log(`[useXamanPayloadStatus] Polling: Invoking onExpired callback`);
          onExpired?.();
        } else {
          console.log(`[useXamanPayloadStatus] Polling: SKIPPING duplicate onExpired callback`);
        }
        cleanup();
        return;
      }

      // Transaction successfully dispatched
      if (signed && dispatched === 'tesSUCCESS') {
        setState(prev => ({ ...prev, status: 'signed', txid }));
        // Guard against duplicate callback invocations
        if (!callbackInvokedRef.current) {
          callbackInvokedRef.current = true;
          console.log(`[useXamanPayloadStatus] Polling: Invoking onSigned callback with txid: ${txid}`);
          onSigned?.(txid);
        } else {
          console.log(`[useXamanPayloadStatus] Polling: SKIPPING duplicate onSigned callback`);
        }
        cleanup();
        return;
      }

      // Transaction dispatched but failed
      if (signed && dispatched && dispatched !== 'tesSUCCESS') {
        setState(prev => ({
          ...prev,
          status: 'rejected',
          error: `Transaction failed: ${dispatched}`
        }));
        // Guard against duplicate callback invocations
        if (!callbackInvokedRef.current) {
          callbackInvokedRef.current = true;
          console.log(`[useXamanPayloadStatus] Polling: Invoking onRejected callback (failed: ${dispatched})`);
          onRejected?.();
        } else {
          console.log(`[useXamanPayloadStatus] Polling: SKIPPING duplicate onRejected callback`);
        }
        cleanup();
        return;
      }

      // User signed but transaction still dispatching
      if (signed && !dispatched) {
        setState(prev => ({ ...prev, status: 'dispatching' }));
        scheduleNextPoll();
        return;
      }

      // User rejected (signed=false, resolved=true)
      if (signed === false && resolved === true) {
        setState(prev => ({
          ...prev,
          status: 'rejected',
          error: 'User rejected the transaction'
        }));
        // Guard against duplicate callback invocations
        if (!callbackInvokedRef.current) {
          callbackInvokedRef.current = true;
          console.log(`[useXamanPayloadStatus] Polling: Invoking onRejected callback (user rejected)`);
          onRejected?.();
        } else {
          console.log(`[useXamanPayloadStatus] Polling: SKIPPING duplicate onRejected callback`);
        }
        cleanup();
        return;
      }

      // Still pending — schedule next poll
      scheduleNextPoll();
    } catch (err) {
      // On 429 rate limit, double the interval (exponential backoff)
      if (err instanceof ApiHttpError && err.status === 429) {
        currentIntervalRef.current = Math.min(currentIntervalRef.current * 2, MAX_BACKOFF_MS);
        console.warn(`[useXamanPayloadStatus] Rate limited, backing off to ${currentIntervalRef.current}ms`);
      } else {
        console.error('[useXamanPayloadStatus] Polling error:', err);
      }
      // Continue polling on error
      scheduleNextPoll();
    }
  };

  // After WebSocket reports signed:true, verify the dispatch result via HTTP.
  // The WebSocket doesn't include the XRPL result code, so we need to check
  // whether the transaction actually succeeded (tesSUCCESS) or failed (tec*).
  const verifyDispatchResult = async (wsTxid: string) => {
    const maxAttempts = 10;
    const delay = 2000;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (isCleanedUpRef.current) return;

      try {
        const response = await apiClient.get<any>(`/offers/payload/${uuid}`);
        if (!response.success || !response.data) {
          await new Promise(r => setTimeout(r, delay));
          continue;
        }

        const { dispatched, txid } = response.data;
        const finalTxid = txid || wsTxid;

        if (dispatched === 'tesSUCCESS') {
          setState(prev => ({ ...prev, status: 'signed', txid: finalTxid }));
          if (!callbackInvokedRef.current) {
            callbackInvokedRef.current = true;
            console.log(`[useXamanPayloadStatus] Verified dispatch success, txid: ${finalTxid}`);
            onSigned?.(finalTxid);
          }
          return;
        }

        if (dispatched && dispatched !== 'tesSUCCESS') {
          setState(prev => ({
            ...prev,
            status: 'rejected',
            error: `Transaction failed: ${dispatched}`,
          }));
          if (!callbackInvokedRef.current) {
            callbackInvokedRef.current = true;
            console.log(`[useXamanPayloadStatus] Dispatch failed: ${dispatched}`);
            onRejected?.();
          }
          return;
        }

        // dispatched not yet available — wait and retry
        console.log(`[useXamanPayloadStatus] Verify attempt ${attempt}/${maxAttempts}: dispatch result pending`);
        await new Promise(r => setTimeout(r, delay));
      } catch (err) {
        console.error('[useXamanPayloadStatus] Verify dispatch error:', err);
        await new Promise(r => setTimeout(r, delay));
      }
    }

    // Exhausted retries — fall back to treating WebSocket txid as success
    // (preserves existing behavior if the HTTP endpoint is unreachable)
    console.warn('[useXamanPayloadStatus] Could not verify dispatch result, falling back to WS txid');
    setState(prev => ({ ...prev, status: 'signed', txid: wsTxid }));
    if (!callbackInvokedRef.current) {
      callbackInvokedRef.current = true;
      onSigned?.(wsTxid);
    }
  };

  return state;
}
